/**
 * The chat's pure half (change spec driver-office-chat, design 7). No IO: path building, the file
 * checks the bucket will make anyway (so the user hears it before an upload), the error -> copy map,
 * the hint parser, day grouping and the inbox clock, all in the AGENCY's time zone (the local-time rule:
 * the US has several zones, the browser's is not the agency's).
 */
import type { RpcError } from '@/data/rpc';

/** The private bucket the chat's photos and PDFs live in (0091). */
export const CHAT_BUCKET = 'chat-files';
/** The body cap (0091 check + chat_send 22023). */
export const CHAT_MAX_BODY = 4000;
/** The composer shows a counter from here on. */
export const CHAT_COUNTER_FROM = 3800;
/** The bucket's file_size_limit (10 MB). */
export const CHAT_MAX_FILE_BYTES = 10 * 1024 * 1024;
/** The bucket's allowed_mime_types, and the extension each one is stored with. */
export const CHAT_MIME_EXT: Record<string, string> = {
  'image/jpeg': 'jpg',
  'image/png': 'png',
  'image/webp': 'webp',
  'application/pdf': 'pdf',
};
/** Newest page on open, and each "Earlier messages" page. */
export const CHAT_PAGE = 50;
/** A `since` cursor page: loop while a full page comes back. */
export const CHAT_CURSOR_PAGE = 100;
/** Signed URLs of attachments live 60 s (private bucket, never cached). */
export const CHAT_SIGNED_URL_TTL = 60;

export type ChatSide = 'driver' | 'office';

/** One chat_messages row as the portal uses it. */
export interface ChatMessage {
  id: string;
  threadId: string;
  seq: number;
  clientId: string;
  senderUserId: string | null;
  senderSide: ChatSide;
  senderName: string | null;
  body: string | null;
  loadId: string | null;
  attachmentPath: string | null;
  attachmentMime: string | null;
  attachmentSize: number | null;
  attachmentName: string | null;
  createdAt: string;
}

const str = (v: unknown): string | null => (typeof v === 'string' ? v : null);
const num = (v: unknown): number | null => {
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  if (typeof v === 'string' && v !== '' && Number.isFinite(Number(v))) return Number(v);
  return null;
};

/** A PostgREST chat_messages row (bigint may arrive as a string) -> ChatMessage; null when unusable. */
export function toChatMessage(r: Record<string, unknown>): ChatMessage | null {
  const id = str(r.id);
  const threadId = str(r.thread_id);
  const seq = num(r.seq);
  const side = r.sender_side === 'driver' || r.sender_side === 'office' ? r.sender_side : null;
  if (!id || !threadId || seq === null || !side) return null;
  return {
    id,
    threadId,
    seq,
    clientId: str(r.client_id) ?? '',
    senderUserId: str(r.sender_user_id),
    senderSide: side,
    senderName: str(r.sender_name),
    body: str(r.body),
    loadId: str(r.load_id),
    attachmentPath: str(r.attachment_path),
    attachmentMime: str(r.attachment_mime),
    attachmentSize: num(r.attachment_size),
    attachmentName: str(r.attachment_name),
    createdAt: str(r.created_at) ?? '',
  };
}

/** Merge pages by seq: one row per seq, ascending (a hint and a poll that overlap never duplicate). */
export function mergeMessages(current: readonly ChatMessage[], incoming: readonly ChatMessage[]): ChatMessage[] {
  const bySeq = new Map<number, ChatMessage>();
  for (const m of current) bySeq.set(m.seq, m);
  for (const m of incoming) bySeq.set(m.seq, m);
  return [...bySeq.values()].sort((a, b) => a.seq - b.seq);
}

/** `{agency}/{thread}/{uuid}.{ext}`: exactly two folders, the bucket policy's shape (0091). */
export function chatObjectPath(agencyId: string, threadId: string, objectId: string, mime: string): string {
  const ext = CHAT_MIME_EXT[mime] ?? 'bin';
  return `${agencyId}/${threadId}/${objectId}.${ext}`;
}

export interface FileLike {
  name: string;
  type: string;
  size: number;
}

/** The composer's own check, the same limits as the bucket: null = fine. */
export function checkChatFile(file: FileLike): 'tooBig' | 'badType' | null {
  if (!Object.prototype.hasOwnProperty.call(CHAT_MIME_EXT, file.type)) return 'badType';
  if (file.size <= 0 || file.size > CHAT_MAX_FILE_BYTES) return 'tooBig';
  return null;
}

/** The body as chat_send will store it: trimmed; blank -> null. */
export function normalizeBody(text: string): string | null {
  const t = text.trim();
  return t === '' ? null : t;
}

/**
 * A failed send / read -> the `chat.errors.*` key (design 7.7). SQLSTATE first; Storage failures carry
 * `storage:<status>` (the useLoadDocuments convention).
 */
export function chatErrorKey(err: RpcError | null | undefined): string {
  const code = err?.code ?? '';
  const message = err?.message ?? '';
  if (code === '42501') return 'chat.errors.notAllowed';
  if (code === 'P0002') return 'chat.errors.gone';
  if (code === '55000') return /paused/.test(message) ? 'chat.errors.paused' : 'chat.errors.removed';
  if (code === '22023' && /longer than 4000/.test(message)) return 'chat.errors.tooLong';
  if (code === 'storage:413' || /maximum allowed size|payload too large/i.test(message)) return 'chat.errors.tooBig';
  if (code === 'storage:415' || /mime type/i.test(message)) return 'chat.errors.badType';
  return 'chat.errors.generic';
}

/** A Storage "409" (the object is already there) on a retry means the bytes landed: go on to chat_send. */
export function isAlreadyUploaded(err: { statusCode?: string | number; status?: number; message?: string } | null | undefined): boolean {
  if (!err) return false;
  const code = String(err.statusCode ?? err.status ?? '');
  return code === '409' || /already exists/i.test(err.message ?? '');
}

/** The realtime hints (design 3.3): ids and the seq only. */
export type ChatHint =
  | {
      type: 'chat.message';
      threadId: string;
      agencyId: string;
      driverId: string;
      messageId: string;
      seq: number;
      senderSide: ChatSide;
      loadId: string | null;
      at: string;
    }
  | { type: 'chat.read'; threadId: string; agencyId: string; driverId: string; side: ChatSide; readSeq: number; at: string };

/** A relay payload -> a ChatHint, or null for anything else (a load hint, garbage). Never throws. */
export function parseChatHint(data: unknown): ChatHint | null {
  if (typeof data !== 'object' || data === null) return null;
  const r = data as Record<string, unknown>;
  const threadId = str(r.thread_id);
  const agencyId = str(r.agency_id);
  const driverId = str(r.driver_id);
  if (!threadId || !agencyId || !driverId) return null;
  const at = str(r.at) ?? '';
  if (r.type === 'chat.message') {
    const messageId = str(r.message_id);
    const seq = num(r.seq);
    const side = r.sender_side === 'driver' || r.sender_side === 'office' ? r.sender_side : null;
    if (!messageId || seq === null || !side) return null;
    return { type: 'chat.message', threadId, agencyId, driverId, messageId, seq, senderSide: side, loadId: str(r.load_id), at };
  }
  if (r.type === 'chat.read') {
    const readSeq = num(r.read_seq);
    const side = r.side === 'driver' || r.side === 'office' ? r.side : null;
    if (readSeq === null || !side) return null;
    return { type: 'chat.read', threadId, agencyId, driverId, side, readSeq, at };
  }
  return null;
}

/** `YYYY-MM-DD` of an instant in a zone. */
export function ymdInZone(iso: string, timeZone: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return new Intl.DateTimeFormat('en-CA', { timeZone, year: 'numeric', month: '2-digit', day: '2-digit' }).format(d);
}

function daysBetween(fromYmd: string, toYmd: string): number {
  return Math.round((Date.parse(`${toYmd}T00:00:00Z`) - Date.parse(`${fromYmd}T00:00:00Z`)) / 86_400_000);
}

export interface DayGroup<T> {
  /** `YYYY-MM-DD` in the agency zone. */
  ymd: string;
  /** 'today' | 'yesterday' | 'date' (the caller formats the date). */
  label: 'today' | 'yesterday' | 'date';
  items: T[];
}

/** Messages (ascending) grouped by their day in the agency zone. */
export function groupByDay<T extends { createdAt: string }>(items: readonly T[], timeZone: string, now: Date = new Date()): DayGroup<T>[] {
  const today = ymdInZone(now.toISOString(), timeZone);
  const out: DayGroup<T>[] = [];
  for (const m of items) {
    const ymd = ymdInZone(m.createdAt, timeZone);
    let g = out[out.length - 1];
    if (!g || g.ymd !== ymd) {
      const diff = today && ymd ? daysBetween(ymd, today) : Number.NaN;
      g = { ymd, label: diff === 0 ? 'today' : diff === 1 ? 'yesterday' : 'date', items: [] };
      out.push(g);
    }
    g.items.push(m);
  }
  return out;
}

/** The inbox clock (design 7.2): today -> the time, this week -> the weekday, older -> the date. */
export function inboxTime(iso: string | null, timeZone: string, locale: string, now: Date = new Date()): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const diff = daysBetween(ymdInZone(iso, timeZone), ymdInZone(now.toISOString(), timeZone));
  if (diff <= 0) return new Intl.DateTimeFormat(locale, { timeZone, hour: 'numeric', minute: '2-digit' }).format(d);
  if (diff < 7) return new Intl.DateTimeFormat(locale, { timeZone, weekday: 'short' }).format(d);
  return new Intl.DateTimeFormat(locale, { timeZone, month: 'short', day: 'numeric' }).format(d);
}

/** A bubble's time of day in the agency zone. */
export function timeInZone(iso: string, timeZone: string, locale: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return new Intl.DateTimeFormat(locale, { timeZone, hour: 'numeric', minute: '2-digit' }).format(d);
}

/** The id of the office message that carries "Seen": the last office message at or below the driver's mark. */
export function seenMessageId(messages: readonly ChatMessage[], driverReadSeq: number): string | null {
  for (let i = messages.length - 1; i >= 0; i -= 1) {
    const m = messages[i]!;
    if (m.senderSide === 'office' && m.seq <= driverReadSeq) return m.id;
  }
  return null;
}

/** The badge text: 1..99, then 99+; nothing at 0. */
export function badgeText(n: number): string | null {
  if (!Number.isFinite(n) || n <= 0) return null;
  return n > 99 ? '99+' : String(n);
}
